import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useState, useEffect, useContext, useCallback, useReducer, useMemo, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import styled from 'styled-components';
import { PopoverStatusContext } from "../Popover/Positioner";
import { DescendantsProvider } from "../../hooks/useDescendants";
import { MenuListContext } from "./MenuListContext";
import { getFirstEnabledChildIndex, getLastEnabledChildIndex, getNextEnabledChild, getPrevEnabledChild, findItemFromTypeahead, getChildIndexById } from "../../hooks/useDescendants/traversal-helpers";
import { MovingTowardSubmenuProvider } from "./useMovingTowardSubmenu";
var defaultMenuListState = {
  selectedId: null,
  expandedId: null,
  shouldFocusChild: false,
  typeahead: '',
  mouse: false
};

function menuListReducer(state, action) {
  switch (action.type) {
    case 'SELECT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedId: action.payload.id,
          shouldFocusChild: false,
          mouse: action.payload.mouse
        });
      }

    case 'DESELECT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedId: null,
          shouldFocusChild: false
        });
      }

    case 'COLLAPSE':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          expandedId: null
        });
      }

    case 'COLLAPSE_AND_DESELECT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          expandedId: null,
          selectedId: null,
          shouldFocusChild: false
        });
      }

    case 'SELECT_AND_EXPAND':
      {
        var id = action.payload.id;
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedId: id,
          expandedId: id,
          mouse: action.payload.mouse
        });
      }

    case 'SELECT_AND_COLLAPSE':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedId: action.payload.id,
          expandedId: null,
          shouldFocusChild: false,
          mouse: action.payload.mouse
        });
      }

    case 'EXPAND_AND_FOCUS_CHILD':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          expandedId: action.payload.id,
          selectedId: null,
          shouldFocusChild: true
        });
      }

    case 'SEARCH':
      {
        if (typeof action.payload !== 'undefined') {
          return _objectSpread(_objectSpread({}, state), {}, {
            typeahead: action.payload
          });
        }

        return state;
      }

    default:
      return state;
  }
}

var StyledMenuList = styled.div.withConfig({
  displayName: "MenuList__StyledMenuList",
  componentId: "vapor__sc-1nisxua-0"
})(["min-width:", ";max-width:", ";width:", ";outline:none;padding:0;"], function (p) {
  return p.$minWidth;
}, function (p) {
  return p.$maxWidth;
}, function (p) {
  return p.$width;
});
/**
 * MenuList
 *
 * Controls keyboard navigation and selected / expanded
 * states for a collection of menu items.
 */

export var MenuList = React.forwardRef(function (_ref, forwardedRef) {
  var _ref$minWidth = _ref.minWidth,
      minWidth = _ref$minWidth === void 0 ? '200px' : _ref$minWidth,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? '350px' : _ref$maxWidth,
      width = _ref.width,
      children = _ref.children,
      other = _objectWithoutProperties(_ref, ["minWidth", "maxWidth", "width", "children"]);

  var menuRef = useRef(null);

  var _useContext = useContext(PopoverStatusContext),
      isShowing = _useContext.isShowing;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      descendants = _useState2[0],
      setDescendants = _useState2[1];

  var _useReducer = useReducer(menuListReducer, defaultMenuListState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      _useReducer2$ = _useReducer2[0],
      shouldFocusChild = _useReducer2$.shouldFocusChild,
      expandedId = _useReducer2$.expandedId,
      selectedId = _useReducer2$.selectedId,
      typeahead = _useReducer2$.typeahead,
      mouse = _useReducer2$.mouse,
      dispatch = _useReducer2[1];

  var _useContext2 = useContext(MenuListContext),
      parentWantsToFocusFirstChild = _useContext2.shouldFocusChild;
  /**
   * Select the first enabled MenuItem child of a MenuList when our parent
   * Menu (or MenuList) wants us to focus that child. This is used
   * when a MenuList is opened via keyboard.
   */


  useEffect(function () {
    if (isShowing && parentWantsToFocusFirstChild && descendants.length) {
      var firstEnabledChild = descendants[getFirstEnabledChildIndex(descendants)];

      if (firstEnabledChild) {
        firstEnabledChild.element.current.focus();
        dispatch({
          type: 'SELECT',
          payload: {
            id: firstEnabledChild.id,
            mouse: false
          }
        });
      }
    }
  }, [descendants, isShowing, parentWantsToFocusFirstChild]);
  /**
   * Handle "Typeahead" functionality.
   */

  useEffect(function () {
    var matchingChild = findItemFromTypeahead(descendants, typeahead);

    if (typeahead && matchingChild) {
      dispatch({
        type: 'SELECT_AND_COLLAPSE',
        payload: {
          id: matchingChild.id,
          mouse: false
        }
      });
    } // Reset our search query after 750ms.


    var timeout = window.setTimeout(function () {
      return typeahead && dispatch({
        type: 'SEARCH',
        payload: ''
      });
    }, 750);
    return function () {
      return window.clearTimeout(timeout);
    };
  }, [descendants, typeahead]);
  /**
   * Handle keyboard accessibility.
   *
   * We handle most of these events at the MenuList
   * level because we can't assume that a MenuItem
   * will always have a focus state.
   */

  var handleKeyDown = useCallback(function (e) {
    function handleEvent() {
      e.stopPropagation();
      e.preventDefault();
    }

    switch (e.key) {
      // Select the first enabled child.
      case 'Home':
        {
          handleEvent();
          var firstEnabledChild = descendants[getFirstEnabledChildIndex(descendants)];

          if (firstEnabledChild) {
            dispatch({
              type: 'SELECT_AND_COLLAPSE',
              payload: {
                id: firstEnabledChild.id,
                mouse: false
              }
            });
          }

          break;
        }
      // Collapse our MenuList.

      case 'ArrowLeft':
        {
          if (expandedId) {
            handleEvent();
            dispatch({
              type: 'SELECT_AND_COLLAPSE',
              payload: {
                id: expandedId,
                mouse: false
              }
            });
          }

          break;
        }
      // Select the last enabled child.

      case 'End':
        {
          handleEvent();
          var lastEnabledChild = descendants[getLastEnabledChildIndex(descendants)];

          if (lastEnabledChild) {
            dispatch({
              type: 'SELECT_AND_COLLAPSE',
              payload: {
                id: lastEnabledChild.id,
                mouse: false
              }
            });
          }

          break;
        }
      // Select the next enabled child.

      case 'ArrowDown':
        {
          handleEvent();
          var nextEnabledChild = getNextEnabledChild(selectedId, descendants);

          if (nextEnabledChild) {
            dispatch({
              type: 'SELECT_AND_COLLAPSE',
              payload: {
                id: nextEnabledChild.id,
                mouse: false
              }
            });
          }

          break;
        }
      // Select the previous enabled child.

      case 'ArrowUp':
        {
          handleEvent();
          var prevEnabledChild = getPrevEnabledChild(selectedId, descendants);
          dispatch({
            type: 'SELECT_AND_COLLAPSE',
            payload: {
              id: prevEnabledChild.id,
              mouse: false
            }
          });
          break;
        }
      // Prevent default tab behavior.

      case 'Tab':
        handleEvent();
        break;

      default:
        // Trigger typeahead functionality
        if (typeof e.key === 'string' && e.key.length === 1) {
          handleEvent();
          dispatch({
            type: 'SEARCH',
            payload: typeahead + e.key.toLowerCase()
          });
          break;
        }

    }
  }, [descendants, expandedId, typeahead, selectedId]);
  /**
   * On mouse-enter, collapse any expanded submenus.
   */

  var handleMouseEnter = useCallback(function (e) {
    if (expandedId) {
      var expandedIndex = getChildIndexById(expandedId, descendants);
      var expandedElement = descendants[expandedIndex].containerRef.current;

      if (expandedElement && !expandedElement.contains(e.target)) {
        dispatch({
          type: 'COLLAPSE'
        });
        menuRef.current.focus();
      }
    }
  }, [descendants, expandedId, menuRef]);
  var menuContext = useMemo(function () {
    return {
      shouldFocusChild: shouldFocusChild,
      expandedId: expandedId,
      selectedId: selectedId,
      dispatch: dispatch,
      menuRef: menuRef,
      mouse: mouse
    };
  }, [expandedId, mouse, selectedId, shouldFocusChild]);
  return /*#__PURE__*/React.createElement(StyledMenuList, _extends({
    ref: mergeRefs([menuRef, forwardedRef]),
    tabIndex: -1,
    "aria-orientation": "vertical",
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseEnter,
    $minWidth: minWidth,
    $maxWidth: maxWidth,
    $width: width
  }, other), /*#__PURE__*/React.createElement(DescendantsProvider, {
    descendants: descendants,
    setDescendants: setDescendants
  }, /*#__PURE__*/React.createElement(MovingTowardSubmenuProvider, null, /*#__PURE__*/React.createElement(MenuListContext.Provider, {
    value: menuContext
  }, children))));
});
export var testExports = {
  menuListReducer: menuListReducer,
  defaultMenuListState: defaultMenuListState
};